import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'
import { DAIMI_HERO_PHOTO, DAIMI_SLUG, DAIMI_STATS } from '@/content/daimi'

/**
 * The Türkiye Dijital ve Yapay Zeka Olgunluk Raporu 2026 page — its copy and its
 * figures, apart from the component that draws them.
 *
 * **Why this report has a page and twenty-seven others do not.** It is the fifth
 * designed report page, after `content/daimi.ts`, `daimiConsumerGoods.ts`,
 * `daimiUsa.ts` and `daimiAirports.ts`, asked for on 2026-08-27 — *"daimi türkiye
 * özel"* — with five attachments: two thumbnails of the live digitopia.co page and
 * three figures. Until then the Türkiye edition was drawn by the shared two-column
 * template in `pages/Reports.tsx` over the series description, and carried a
 * `stillToCome({ copy: true })` note saying exactly that.
 *
 * **This page is written in Turkish, and that is not a translation decision.** The
 * report is Türkiye's, its title and its PDF are Turkish, and the live page is
 * Turkish. The report pages are all emitted under `/en/` (`prerender.tsx` says so
 * — they have no locale siblings), so the *document* is `lang="en"` while this
 * page's prose is not: `pages/DaimiTurkiye.tsx` wraps the whole page in
 * `lang="tr"` so a screen reader and a search engine are told the truth about the
 * words rather than about the folder they sit in.
 *
 * **What was supplied, and what was not — because the difference decides every
 * amber note below.** The two page captures are *thumbnails*: 383px wide for a
 * full page, which renders body copy at about three pixels. So unlike the USA
 * Edition page, whose copy is a transcription off a 13,700px capture, **nothing
 * here is transcribed from the live page except the headings**, which are the only
 * type large enough to read. The band order, the band backgrounds and the section
 * headings are certain. The prose under them is *written* — from the Turkish DAIMI
 * description this repository already holds (the imported
 * `confirmation/turkiye-dijital-ve-yapay-zeka-olgunluk-raporu-2026` page) and from
 * the series' own findings as the USA Edition page states them — and it says so,
 * per band, in amber.
 *
 * **The numbers are the exception, and they are real.** `TR_figure02` — attached
 * as `daimi-maturity-index-turkiye.webp` — is legible, so the Türkiye/global index
 * comparison below is read straight off the chart the page shows: DMI 2.97 against
 * 3.06, DAIMI 2.83 against 2.93, AIMI 2.30 against 2.38. Nothing else on this page
 * states a figure that is not either in that chart or already verified in
 * `content/daimi.ts`. The live page's counts table and its sector chart are not
 * reproduced with invented values — the counts band shows the global column we
 * hold and asks for the Türkiye one, and the sector band asks for the export.
 */

/** The report this page belongs to — the branch `pages/Reports.tsx` takes on it. */
export const DAIMI_TR_SLUG = 'turkiye-dijital-ve-yapay-zeka-olgunluk-raporu-2026'

/**
 * The hero headline, split the way the live page sets it: the subject on the first
 * line, the report's name and year under it in the accent.
 *
 * Two fields rather than one string with markup in it — the break is a design
 * decision about this page, not punctuation inside a sentence. Same shape as
 * `DAIMI_USA_TITLE`.
 */
export const DAIMI_TR_TITLE = {
  lead: 'Türkiye Dijital ve Yapay Zeka',
  accent: 'Olgunluk Raporu 2026',
}

export const DAIMI_TR_INTRO = {
  body: [
    'Teknoloji seviye atladı; kurumsal olgunluk aynı hızda ilerlemedi. Türkiye Dijital ve Yapay Zeka Olgunluk Raporu 2026, Türkiye’deki kurumların global benchmark karşısında nerede durduğunu aynı DAIMI değerlendirmesiyle ölçer ve dijital ile yapay zeka kabiliyetlerini ölçülebilir iş sonuçlarına çevirmenin yolunu gösterir.',
  ],
  /**
   * The globe, in a dark card on the right of the hero — where the live page puts
   * the same artwork, and the same file the flagship report's hero and the USA
   * Edition page already run. Decorative, so `alt` stays empty: the headline
   * beside it says everything it says.
   */
  photo: DAIMI_HERO_PHOTO,
  check:
    'Bu paragraf canlı sayfadan okunarak alınmadı — gönderilen ekran görüntüsü 383 piksel genişliğinde bir küçük resim ve gövde metni okunmuyor. Rapor hakkında doğru olduğunu bildiğimiz şeylerden yazıldı. Ayrıca bu metin arama sonucunda da görünür (`content/reports.ts` meta description’ı buradan alır), o yüzden production öncesi digitopia.co ile karşılaştırılmalı ya da raporun kendi giriş metni gönderilmeli.',
}

/**
 * The counts band — the live page's "Rakamlarla 2026 Benchmark" table.
 *
 * **Both columns carry values now, and the Türkiye ones are supplied rather than
 * guessed.** The band shipped on 2026-08-27 with an empty Türkiye column and an
 * amber note asking for it, because the counts were illegible in the 383px
 * thumbnails. On 2026-08-28 the table was sent as a full-size capture —
 * *"türkiye raporundaki rakamlarla 2026 benchmark kısmı bu şekilde olmalı"* — so
 * the note is resolved: three rows, not four, and each cell states its own unit
 * the way the report's table does.
 *
 * Türkiye's three figures corroborate independently against the WordPress import
 * this repository already holds: `imported/pages/tr/sanayi-ve-teknoloji-bakanligi-yatirim-tesvik`
 * says *"551 ölçüm, 11.847 katılımcı ve 36.000’den fazla veri noktası"*.
 *
 * **The first row is asymmetric on purpose** — it is asymmetric in the report.
 * Türkiye is one country, so where the global column counts countries and
 * sectors, the Türkiye column states the measurement count instead. Do not
 * "fix" it into a countries figure of 1.
 */
/**
 * A global figure, written the way Turkish writes it.
 *
 * `DAIMI_STATS` holds "33,870" because the flagship page is English. Turkish uses
 * the full stop as the thousands separator, so "33.870" — and folding it here
 * keeps one source for the number itself. Restating the values as Turkish
 * literals would work today and drift the first time the benchmark is restated.
 */
function tr(value: string): string {
  return value.replace(/,/g, '.')
}

export const DAIMI_TR_NUMBERS = {
  heading: 'Rakamlarla 2026 Benchmark',
  lede: 'Türkiye raporu, global raporla aynı DAIMI değerlendirmesinin Türkiye kesitidir: aynı sorular, aynı endeks, aynı ölçüm dönemi.',
  columns: { global: 'Global rapor', turkiye: 'Türkiye raporu' },
  rows: [
    {
      label: 'Ülke & Sektör',
      /** 46 is the report's own country count; the sector count is `DAIMI_STATS`. */
      global: `46 Ülke, ${tr(DAIMI_STATS[3].value)} Sektör`,
      turkiye: '551 Ölçüm',
    },
    {
      label: 'Katılımcı',
      global: `${tr(DAIMI_STATS[1].value)} Katılımcı`,
      turkiye: '11.847 Katılımcı',
    },
    {
      label: 'Veri Noktası',
      global: `${tr(DAIMI_STATS[2].value)} Yapılandırılmış Veri`,
      turkiye: '36.201 Yapılandırılmış Veri',
    },
  ],
}

/**
 * The second band: what the index actually says about Türkiye, beside the chart
 * that says it.
 *
 * **The three points restate the chart rather than adding to it**, which is the
 * whole condition under which writing them here is honest: every figure in them is
 * legible in `daimi-maturity-index-turkiye.webp`. The labels are the series' three
 * findings as the USA Edition page states them, rendered in Turkish — not a
 * transcription of the live Turkish page's own bullets, and `check` says so.
 */
export const DAIMI_TR_REALITY = {
  heading: 'Dijitalin Gerçek Tablosu',
  lede: 'Türkiye, global benchmark’ı yakından takip ediyor ama üç ölçümün üçünde de arkasında duruyor. Makasın kendisi küçük; hızlanan bir cephede küçük kalmıyor.',
  points: [
    {
      label: 'Teknoloji seviye atladı, olgunluk yerinde saydı',
      body: 'Türkiye’nin DAIMI skoru 2.83, global skor 2.93. Dijital tarafta zemin sağlam (DMI 2.97), ama yapay zeka olgunluğu 2.30’da, yani dijitalin belirgin biçimde gerisinde.',
    },
    {
      label: 'FOMO gürültü, değer sinyaldir',
      body: 'AIMI 2.30 ile global 2.38’in altında. Fark, pilot sayısında değil; pilotların uçtan uca iş akışlarını ve iş metriklerini değiştirip değiştirmediğinde.',
    },
    {
      label: 'Makas kapanmıyor, açılıyor',
      body: 'DMI 2021’de 2.69’dan 2025’te 2.97’ye çıktı; global endeks aynı dönemde 2.78’den 3.06’ya. İki eğri paralel ilerliyor; yani mesafe kapanmıyor, korunuyor.',
    },
  ],
  photo: {
    src: '/photo/daimi-maturity-index-turkiye.webp',
    alt: 'Olgunluk Endeksi: Global DMI 2020’de 2.70’ten 2025’te 3.06’ya, Türkiye DMI 2021’de 2.69’dan 2025’te 2.97’ye; Global DAIMI 2.93’e karşı Türkiye 2.83; Global AIMI 2.38’e karşı Türkiye 2.30',
  } satisfies Photo,
  caption: 'Şekil 1: Olgunluk Endeksi, 2020–2025. DMI, DAIMI ve AIMI, global ve Türkiye.',
  /** The three comparisons the chart makes, set as type beside it. */
  scores: [
    { label: 'DMI', note: 'Dijital olgunluk', turkiye: '2.97', global: '3.06' },
    { label: 'DAIMI', note: 'Dijital ve yapay zeka olgunluğu', turkiye: '2.83', global: '2.93' },
    { label: 'AIMI', note: 'Yapay zeka olgunluğu', turkiye: '2.30', global: '2.38' },
  ],
  scoresLabel: 'Türkiye / Global, 2025',
  check:
    'Bu banttaki bütün rakamlar gönderilen grafikten (TR_figure02) okundu ve doğrudur. Üç maddenin başlıkları ise canlı sayfadan alınmadı — raporun serideki üç bulgusunun Türkçe karşılığı olarak yazıldı. Canlı sayfanın kendi başlık ve metinleri gönderilirse birebir bunlarla değiştirilir.',
}

/**
 * The third band: the sector cut.
 *
 * **Nothing stands in for the chart, and no sector is named.** The live band shows
 * a wide Türkiye-by-sector benchmark; it was not supplied as a file, and the
 * sector names and their scores were not legible in the thumbnail. The two global
 * benchmark charts this repository holds are the global cut, so putting one under
 * a caption that says "Türkiye" would be a false claim rather than a placeholder —
 * the same call `content/daimiUsa.ts` made about the same chart.
 */
export const DAIMI_TR_SECTORS = {
  heading: 'Kimler Farkı Açıyor?',
  lede: 'Türkiye ortalaması tek başına bir sektörü anlatmaz. Rapor, Türkiye’deki sektörleri global endeks benchmark’ı karşısında tek tek konumlandırır ve yapay zekayı endüstrileştirenlerle hâlâ temeli kuranlar arasındaki mesafeyi görünür kılar.',
  body: 'Sektör grafiğinde her sektör iki değerle okunur: global endeks benchmark’ı ve o sektörün Türkiye skoru. Aradaki fark, sektörün kendi küresel rakiplerine göre nerede durduğudur.',
  caption: 'Şekil 2: Türkiye sektör benchmark’ı: global endeks ve Türkiye skoru.',
  check:
    'Bu bandın grafiği (canlı sayfadaki Şekil 2, sektör bazlı Türkiye benchmark’ı) dosya olarak gönderilmedi ve küçük resimde sektör isimleri ile skorlar okunmuyor. Elimizdeki iki benchmark grafiği global kesit, Türkiye kesiti değil — bu yüzden burada hiçbir sektör adı geçmiyor ve hiçbir skor yazılmadı. Grafiğin export’unu gönderin, bant canlı sayfadaki hâline gelsin.',
}

/**
 * The methodology band — dark, centred, the wheel under it.
 *
 * **The paragraph is real supplied Turkish copy**, not a translation written here:
 * it is what digitopia.co's own Turkish confirmation page for this very report
 * says about DAIMI, carried into this repository by the WordPress import
 * (`content/imported/pages/tr/confirmation__turkiye-…-2026.json`). That makes it
 * the one band on this page whose prose needs no amber note.
 *
 * The live band runs a navy-to-magenta gradient behind the wheel; this one is the
 * site's flat `--ink`, the call `pages/Daimi.tsx` and `pages/DaimiUsa.tsx` already
 * made about the same purple — one accent colour, and `src/index.css` says so at
 * the top.
 */
export const DAIMI_TR_FRAMEWORK = {
  heading: 'Dönüşümü Ölçülebilir Kılan Çerçeve',
  body: 'DAIMI (Dijital ve Yapay Zeka Olgunluk Endeksi), kurumunuzun mevcut dijital ve yapay zeka seviyesine dair net bir görünüm sunar. Yetkinliklerinizi ölçer, sektörünüzdeki benzer kurumlarla karşılaştırır ve içgörüleri uygulamaya dönük, önceliklendirilmiş bir yol haritasına dönüştürür.',
  /**
   * The six sides of the wheel, listed as type under the drawing.
   *
   * Not decoration: the Turkish export lost its labels (see `check`), so without
   * this line the band would show six unnamed icons. The English page gets the
   * names from the artwork itself.
   */
  dimensionsLabel: 'Altı boyut',
  dimensions: ['İnsan', 'Müşteri', 'Operasyon', 'İnovasyon', 'Yönetişim', 'Teknoloji'],
  photo: {
    src: '/photo/daimi-framework-wheel-turkce-transparent.png',
    alt: 'DAIMI çarkı: Dijital ve Yapay Zeka Olgunluk Endeksi’nin altı boyutu',
  } satisfies Photo,
  learnHref: LINKS.daimi,
  learnLabel: 'Detaylı bilgi',
  check:
    'Türkçe çarkın 2026-09-01’de gönderilen şeffaf zeminli export’u kullanılıyor: boşlukta duran yeşil “YENİ” rozetleri ve beyaz zemin gitti, artık koyu bandın üstünde doğrudan duruyor. Ama altı boyutun isimleri ve alt başlık listeleri hâlâ export’ta yok — çarkın etrafında sadece ikonlar var — o yüzden altı boyut çarkın altına yazı olarak ekleniyor. Etiketleri içeren bir export gönderin, ya da söyleyin, global (İngilizce) çarka dönelim. Bu banttaki paragraf ise gerçek Türkçe metin: digitopia.co’nun bu raporun kendi onay sayfasından geliyor, yazılmadı.',
}

/**
 * The playbook: three steps, each with a photograph beside it.
 *
 * The three step headings are the one part of this band legible in the second
 * thumbnail — they are set large and bold. The body under each is written, in the
 * same terms the USA Edition page states the same playbook, and `check` says so.
 * The photographs are three we already hold and each matches the scene the live
 * page uses at that step.
 */
export const DAIMI_TR_PLAYBOOK = {
  heading: 'Olgunluğu Değere Dönüştüren DAIMI Oyun Planı',
  lede: '2026’nın en büyük riski, çok fazla yapay zeka işi yapıp çok az sonuç almak. Pilotlar çoğalırken olgunluk yerinde sayabiliyor. Öne geçen kurumlar',
  ledeStrong: 'gerçeği ölçüyor, önceliklerini seçiyor ve kararlılıkla uyguluyor.',
  steps: [
    {
      heading: 'Olgunluğunuzu Ölçün: Kontrolü Ele Alın',
      body: 'Ölçmediğiniz şeyi yönetemezsiniz. DAIMI değerlendirmesi kanaati kanıtla değiştirir; nerede güçlü, nerede açık olduğunuzu net biçimde ortaya koyar.',
      photo: {
        src: '/photo/open-office-team.jpg',
        alt: 'Açık ofiste birlikte çalışan Digitopia ekibi; arkalarındaki beyaz tahtada bir plan çizili.',
      } satisfies Photo,
    },
    {
      heading: 'Stratejinizi Belirleyin: Teknoloji İşe Hizmet Etsin',
      body: 'Strateji bir dilek listesi değil, bir tercihler kümesidir. İşin “neden”inden başlayın (büyüme, maliyet ya da dayanıklılık) ve bunu kurmanız gereken somut yetkinliklere çevirin.',
      photo: {
        src: '/photo/daimi-score-presentation.jpg',
        alt: 'Bir Digitopia danışmanı, iki yöneticiye DAIMI skorunu boyut boyut hedefle karşılaştırarak anlatıyor.',
      } satisfies Photo,
    },
    {
      heading: 'Yol Haritanızı Yürütün: Öncelikleri Değere Çevirin',
      body: 'Kazananlar az sayıda yüksek değerli inisiyatife odaklanır ve onları kurum geneline yayar. Hızı ilerlemeyle karıştırmazlar: bir pilot ancak production’a çıkıp sonucu değiştirdiyse başarılıdır.',
      photo: {
        src: '/photo/factory-roadmap.jpg',
        alt: 'Bir Digitopia danışmanı, fabrika sahasındaki ekibe çeyreklik zaman çizelgesi üzerinde dönüşüm yol haritasını anlatıyor.',
      } satisfies Photo,
    },
  ],
  expertLabel: 'Bize Ulaşın',
  expertHref: LINKS.contact,
  check:
    'Üç adımın başlıkları canlı sayfadan okundu; altlarındaki metinler okunamadı ve buraya, aynı oyun planını anlatan USA Edition sayfasının terimleriyle Türkçe yazıldı. Canlı sayfanın kendi metinleri gönderilirse bunlarla değiştirilir. Üçüncü adımın fotoğrafı da bizim eklememiz: canlı sayfada üçüncü adımın yanında görsel yok ve bant yarım kalıyor.',
}

/**
 * The closing band, which is where the live page's gated form sits.
 *
 * Two columns, not a centred block — the second thumbnail shows the form card on
 * the left and the report standing in 3D on the right. The form itself cannot be
 * reproduced (see `check`), so the left card holds what the reader gets and the
 * button that gets it; the right column is the Türkiye edition's own 3D mockup,
 * attached on 2026-08-27 as `daimi-raporu-2026-turkiye-mockup.webp` — so unlike
 * the USA page, this one stands the *right* cover beside its form.
 *
 * `includes` states nothing the paragraph above it does not already state.
 */
export const DAIMI_TR_DOWNLOAD = {
  heading: 'Dijital ve Yapay Zeka Olgunluğunuzu Stratejik Perspektifle Değerlendirin',
  body: 'Türkiye Dijital ve Yapay Zeka Olgunluk Raporu 2026’nın tamamına ulaşın: Türkiye benchmark’ı, sektör kırılımları ve olgunluğu ölçülebilir değere çeviren DAIMI metodolojisi.',
  cardLabel: 'Raporda neler var',
  includes: [
    'Türkiye’nin 2026 benchmark verisi',
    'Sektör bazlı kırılımlar ve karşılaştırmalar',
    'Değeri ölçeklendiren DAIMI metodolojisi',
  ],
  downloadLabel: 'Raporu İndirin',
  globalLabel: 'Global raporu inceleyin',
  globalHref: `/en/impact-labs/reports/${DAIMI_SLUG}`,
  photo: {
    src: '/photo/daimi-raporu-2026-turkiye-mockup.webp',
    alt: 'Türkiye Dijital ve Yapay Zeka Olgunluk Raporu 2026’nın basılı hâli',
  } satisfies Photo,
  check:
    'digitopia.co’da bu bant bir HubSpot formu — ad, soyad, iş e-postası, şirket, unvan, telefon, ülke, sektör, onay kutusu ve “Gönder” — ve PDF sonrasında e-posta ile geliyor. Bu rapor sayfaları client bundle olmadan prerender ediliyor, yani gömülü bir form script’i hiç çalışmaz ve hiçbir yere post etmeyen bir form hiç formdan kötüdür; sitedeki diğer otuz rapor gibi burada da PDF doğrudan kendi bucket’ımızdan veriliyor. Sayfadaki bütün indirme butonları dosyaya gidiyor. Türkiye edition’ının form arkasında olması gerekiyorsa söyleyin.',
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy
 * checks in — the same mechanism `daimiUsaChecks()` uses, appended to the same
 * list in `reportChecks()`.
 *
 * `'content'` on all of them. One does ask for a file, but what it is really
 * about is a claim: a band captioned "Türkiye sektör benchmark'ı" has to draw
 * Türkiye's cut, and that is a question for whoever holds the data rather than a
 * hole in the build. The counts band no longer appears here — its figures arrived
 * on 2026-08-28.
 *
 * `locale: 'en'` because that is where the document lives — `prerender.tsx` emits
 * every report under `/en/`. The page's prose is Turkish and so are these notes,
 * which is the same split `pages/DaimiTurkiye.tsx` makes with its `lang="tr"`
 * wrapper: the folder is English, the words are not.
 */
export function daimiTurkiyeChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'intro', check: DAIMI_TR_INTRO.check },
    { section: 'reality', check: DAIMI_TR_REALITY.check },
    { section: 'sectors', check: DAIMI_TR_SECTORS.check },
    { section: 'framework', check: DAIMI_TR_FRAMEWORK.check },
    { section: 'playbook', check: DAIMI_TR_PLAYBOOK.check },
    { section: 'download', check: DAIMI_TR_DOWNLOAD.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${DAIMI_TR_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
